import type { ReactNode } from 'react';

import { SurveyHtmlBlock } from './SurveyHtmlBlock';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { ScrollView } from '@/components/ui/scroll-view';
import { VStack } from '@/components/ui/vstack';

interface SurveyOutcomeMessageProps {
  message: string;
  onDone: () => void;
  /**
   * The survey's own completion message, shown in place of `message`.
   *
   * Only ever supplied for an accepted response, which is what makes it safe
   * for a survey to say thank you here and nowhere else: a message that can
   * only render after a successful submit cannot be mistaken for the end of
   * the form.
   */
  html?: string;
  children?: ReactNode;
}

/**
 * The end state of a survey screen: a message and a way out.
 *
 * Content passed as children, such as a poll's results, sits between the
 * message and Done and turns the centered layout into a top-aligned scroll, so
 * content taller than the screen stays reachable. Done comes after that content
 * rather than above it, so the respondent reaches the exit having passed what
 * they were shown.
 */
export function SurveyOutcomeMessage({
  message,
  onDone,
  html,
  children,
}: SurveyOutcomeMessageProps) {
  const messageContent = html ? (
    <VStack className="w-full max-w-screen-sm" space="sm">
      <SurveyHtmlBlock html={html} />
    </VStack>
  ) : (
    <Heading size="md" className="text-center">
      {message}
    </Heading>
  );

  const doneButton = (
    <Button onPress={onDone} action="primary" size="md" className="self-center">
      <ButtonText>Done</ButtonText>
    </Button>
  );

  if (!children) {
    return (
      <VStack className="flex-1 items-center justify-center px-6" space="md">
        {messageContent}

        {doneButton}
      </VStack>
    );
  }

  return (
    <ScrollView className="flex-1" contentContainerStyle={{ paddingBottom: 32 }}>
      <VStack className="mx-auto w-full max-w-screen-md px-6 pt-6" space="2xl">
        <VStack className="items-center" space="md">
          {messageContent}
        </VStack>

        {children}

        {doneButton}
      </VStack>
    </ScrollView>
  );
}
